// The blog composer's fields (Figma step 4 `Blog / Composer`) and their request body. Pure: the modal keeps a
// `BlogDraft` and turns it into the create / update body here. Spec: 2026-11-04-blog-design.md sections 5-7.
import type { BlogInput, BlogParagraphInput, PostItemInput, PostV2, WallItem, WallItemType } from "../../api/content";

export const MAX_PARAGRAPHS = 100;
export const MAX_MEDIA = 200;
export const MAX_TEXT_CHARS = 200_000;
export const MAX_PARAGRAPH_CHARS = 10_000;
export const MAX_ABSTRACT_WORDS = 500;
export const MAX_ABSTRACT_CHARS = 4_096;
export const MAX_CAPTION = 500;
export const MAX_TRANSLATOR = 120;
export const MAX_TRANSLATION_LANGUAGE = 64;
export const MAX_ACKNOWLEDGEMENTS = 5_000;
export const MAX_BIBLIOGRAPHY = 10_000;
export const MAX_AUTHOR = 120;
export const MAX_LANGUAGE = 64;
export const MIN_YEAR = 1000;

/** A piece of media under a paragraph. Photos and videos are uploaded files; audio and links are URLs. */
export interface BlogMedia {
  /** Local React key; not sent. */
  key: string;
  type: WallItemType;
  fileId?: string;
  thumbnailFileId?: string;
  url?: string;
  /** Preview shown in the composer (uploaded or stored). */
  previewUrl?: string;
  title?: string;
  caption: string;
  /** A stored item that had a caption (from an edit): blanking it sends "" to clear it; a new item just omits it. */
  kept?: boolean;
}

export interface BlogParagraphDraft {
  /** Local React key; not sent. */
  key: string;
  /** Present only for a kept (stored) paragraph. */
  id?: string;
  body: string;
  media: BlogMedia[];
}

export interface BlogDraft {
  title: string;
  abstract: string;
  cover: { fileId: string; url: string } | null;
  authorName: string;
  year: string;
  language: string;
  translator: string;
  translationLanguage: string;
  paragraphs: BlogParagraphDraft[];
  acknowledgements: string;
  bibliography: string;
}

export type BlogField =
  | "title" | "abstract" | "cover" | "authorName" | "year" | "language" | "translator" | "translationLanguage"
  | "paragraphs" | "media" | "text" | "acknowledgements" | "bibliography";

/** `badParagraphs`: indexes of paragraphs with neither text nor media, or text over the limit; `badCaptions`: media with a caption over the limit; `paragraphs`: the count is outside 1 to 100. */
export type BlogErrors = Partial<Record<BlogField, true>> & { badParagraphs?: number[]; badCaptions?: { paragraph: number; media: number }[] };

let seq = 0;
export const newKey = (): string => `k${++seq}`;

export const newParagraph = (): BlogParagraphDraft => ({ key: newKey(), body: "", media: [] });

export function newBlogDraft(): BlogDraft {
  return {
    title: "", abstract: "", cover: null, authorName: "", year: "", language: "", translator: "", translationLanguage: "",
    paragraphs: [newParagraph()], acknowledgements: "", bibliography: "",
  };
}

/** Photos and videos come first (group 0), then audio and links (group 1). */
export const isVisual = (t: WallItemType): boolean => t === "PHOTO" || t === "VIDEO";
const group = (m: BlogMedia): number => (isVisual(m.type) ? 0 : 1);
const byGroup = (media: BlogMedia[]): BlogMedia[] => [...media.filter((m) => group(m) === 0), ...media.filter((m) => group(m) === 1)];

function mediaOf(it: WallItem): BlogMedia {
  return {
    key: newKey(), type: it.type, fileId: it.fileId ?? undefined, thumbnailFileId: it.thumbnailFileId ?? undefined,
    url: it.fileId ? undefined : it.url ?? undefined, previewUrl: it.photoUrl ?? it.thumbnailUrl ?? undefined,
    title: it.title ?? undefined, caption: it.caption ?? "", ...(it.caption ? { kept: true } : {}),
  };
}

/** An edit's fields from the stored post (its `blog` facts and paragraphs). */
export function blogDraftOf(p: PostV2): BlogDraft {
  const b = p.blog;
  const paragraphs = (b?.paragraphs ?? []).map((pg): BlogParagraphDraft => ({ key: newKey(), id: pg.id, body: pg.body ?? "", media: byGroup(pg.items.map(mediaOf)) }));
  return {
    title: p.title ?? "",
    abstract: p.description ?? p.caption ?? "",
    cover: b?.coverFileId ? { fileId: b.coverFileId, url: b.coverUrl ?? "" } : null,
    authorName: b?.authorName ?? "",
    year: b?.year != null ? String(b.year) : "",
    language: b?.language ?? "",
    translator: b?.translator ?? "",
    translationLanguage: b?.translationLanguage ?? "",
    paragraphs: paragraphs.length > 0 ? paragraphs : [newParagraph()],
    acknowledgements: b?.acknowledgements ?? "",
    bibliography: b?.bibliography ?? "",
  };
}

/** A word is a run of non-space characters. */
export const wordCount = (s: string): number => (s.match(/\S+/g) ?? []).length;
/** Length in characters (code points), as the server counts them. */
const chars = (s: string): number => [...s].length;

/** Fields that cannot be sent as they are; the composer shows each one's reason after a tap on Post. */
export function blogErrors(d: BlogDraft, now: Date = new Date()): BlogErrors {
  const out: BlogErrors = {};
  if (d.title.trim() === "") out.title = true;
  const abstract = d.abstract.trim();
  if (abstract === "" || wordCount(abstract) > MAX_ABSTRACT_WORDS || chars(abstract) > MAX_ABSTRACT_CHARS) out.abstract = true;
  if (d.cover == null) out.cover = true;
  const author = d.authorName.trim();
  if (author === "" || chars(author) > MAX_AUTHOR) out.authorName = true;
  const year = d.year.trim();
  if (!/^\d{1,4}$/.test(year) || Number(year) < MIN_YEAR || Number(year) > now.getFullYear() + 1) out.year = true;
  const language = d.language.trim();
  if (language === "" || chars(language) > MAX_LANGUAGE) out.language = true;
  if (chars(d.translator.trim()) > MAX_TRANSLATOR) out.translator = true;
  if (chars(d.translationLanguage.trim()) > MAX_TRANSLATION_LANGUAGE) out.translationLanguage = true;
  if (chars(d.acknowledgements.trim()) > MAX_ACKNOWLEDGEMENTS) out.acknowledgements = true;
  if (chars(d.bibliography.trim()) > MAX_BIBLIOGRAPHY) out.bibliography = true;

  if (d.paragraphs.length < 1 || d.paragraphs.length > MAX_PARAGRAPHS) out.paragraphs = true;
  const bad: number[] = [];
  const badCaptions: { paragraph: number; media: number }[] = [];
  let total = 0;
  let media = 0;
  d.paragraphs.forEach((pg, i) => {
    const body = pg.body.trim();
    total += chars(body);
    media += pg.media.length;
    if ((body === "" && pg.media.length === 0) || chars(body) > MAX_PARAGRAPH_CHARS) bad.push(i);
    pg.media.forEach((m, j) => {
      if (chars(m.caption.trim()) > MAX_CAPTION) badCaptions.push({ paragraph: i, media: j });
    });
  });
  if (bad.length > 0) out.badParagraphs = bad;
  if (badCaptions.length > 0) out.badCaptions = badCaptions;
  if (total > MAX_TEXT_CHARS) out.text = true;
  if (media > MAX_MEDIA) out.media = true;
  return out;
}

const orUndef = (s: string): string | undefined => (s.trim() === "" ? undefined : s.trim());

function itemInput(m: BlogMedia): PostItemInput {
  const caption = orUndef(m.caption);
  // The server's caption is a pointer: absent keeps the stored one, "" clears it.
  const base = caption ? { caption } : m.kept ? { caption: "" } : {};
  // An uploaded MP3 is titled by its file name (the server turns it into an audio link item).
  if (m.fileId) return { fileId: m.fileId, ...(m.thumbnailFileId ? { thumbnailFileId: m.thumbnailFileId } : {}), ...(m.type === "AUDIO_URL" && m.title ? { title: m.title } : {}), ...base };
  return { url: m.url!, ...(m.title ? { title: m.title } : {}), ...base };
}

/** The create / update body's parts (call only when `blogErrors` is empty). */
export function toBlogInput(d: BlogDraft): { title: string; description: string; blog: BlogInput; paragraphs: BlogParagraphInput[] } {
  const blog: BlogInput = {
    coverFileId: d.cover!.fileId, authorName: d.authorName.trim(), year: Number(d.year.trim()), language: d.language.trim(),
    ...Object.fromEntries(Object.entries({
      translator: orUndef(d.translator), translationLanguage: orUndef(d.translationLanguage),
      acknowledgements: orUndef(d.acknowledgements), bibliography: orUndef(d.bibliography),
    }).filter(([, v]) => v !== undefined)),
  };
  const paragraphs = d.paragraphs.map((pg): BlogParagraphInput => ({
    ...(pg.id ? { id: pg.id } : {}), body: pg.body.trim(), items: byGroup(pg.media).map(itemInput),
  }));
  // Media only comes through paragraphs: the server refuses top-level items on BLOG, so the key is omitted.
  return { title: d.title.trim(), description: d.abstract.trim(), blog, paragraphs };
}

function swap<T>(list: T[], a: number, b: number): T[] {
  const out = [...list];
  [out[a], out[b]] = [out[b], out[a]];
  return out;
}

/** Paragraph reorder: a no-op at the ends. */
export const moveParagraphUp = (d: BlogDraft, i: number): BlogDraft => (i <= 0 || i >= d.paragraphs.length ? d : { ...d, paragraphs: swap(d.paragraphs, i, i - 1) });
export const moveParagraphDown = (d: BlogDraft, i: number): BlogDraft => (i < 0 || i >= d.paragraphs.length - 1 ? d : { ...d, paragraphs: swap(d.paragraphs, i, i + 1) });

/** Media reorder inside a paragraph: stays inside its group (photos and videos, or audio and links); a no-op at a group's edge. */
export function moveMediaUp(pg: BlogParagraphDraft, i: number): BlogParagraphDraft {
  const m = pg.media;
  if (i <= 0 || i >= m.length || group(m[i - 1]) !== group(m[i])) return pg;
  return { ...pg, media: swap(m, i, i - 1) };
}
export function moveMediaDown(pg: BlogParagraphDraft, i: number): BlogParagraphDraft {
  const m = pg.media;
  if (i < 0 || i >= m.length - 1 || group(m[i + 1]) !== group(m[i])) return pg;
  return { ...pg, media: swap(m, i, i + 1) };
}

/** Add media, keeping the groups in display order (a new photo lands after the last photo or video). */
export const addMedia = (pg: BlogParagraphDraft, m: BlogMedia): BlogParagraphDraft => ({ ...pg, media: byGroup([...pg.media, m]) });
